// Render an on-screen element to a downloadable PDF so it looks and prints
// IDENTICALLY on every browser and printer (sidesteps Chrome's fit-to-printer
// scaling). The heavy libraries are code-split (imported on demand) so they never
// bloat the main app bundle.
//
// `fit`:
//   "width"   (default) — fill the page width and paginate onto more Letter pages
//                         if the content is taller than one page.
//   "contain"           — scale to fit within one page, preserving aspect ratio,
//                         centered both ways (single-page marketing docs like the
//                         flyer).
//   "page"              — single Letter page, TOP-anchored: fill the width, and if
//                         the content is a hair too tall, scale down uniformly so
//                         the whole thing lands on ONE page (matches the browser's
//                         own fit-to-page print). Use for letters.
export async function downloadElementAsPdf(element, filename, options) {
  if (!element) return;
  const fit = (options && options.fit) || "width";
  const [{ default: html2canvas }, jspdfModule] = await Promise.all([
    import("html2canvas"),
    import("jspdf")
  ]);
  const JsPdf = jspdfModule.jsPDF || jspdfModule.default;

  const canvas = await html2canvas(element, {
    scale: 3, // 3x for crisp text
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false
  });
  const imgData = canvas.toDataURL("image/jpeg", 0.95);

  const pdf = new JsPdf({ unit: "in", format: "letter", orientation: "portrait" });
  const pageWidth = 8.5;
  const pageHeight = 11;
  const margin = 0.4;
  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;
  const canvasRatio = canvas.width / canvas.height;

  if (fit === "contain") {
    let width = usableWidth;
    let height = width / canvasRatio;
    if (height > usableHeight) {
      height = usableHeight;
      width = height * canvasRatio;
    }
    const x = (pageWidth - width) / 2;
    const y = (pageHeight - height) / 2;
    pdf.addImage(imgData, "JPEG", x, y, width, height);
  } else if (fit === "page") {
    // Single page, top-anchored. Fill the width; if the rendered content is a bit
    // taller than one page, scale it down uniformly so the whole document fits on
    // ONE Letter page (never a 2nd page with an orphaned footer).
    let width = usableWidth;
    let height = width / canvasRatio;
    if (height > usableHeight) {
      const scale = usableHeight / height;
      width = width * scale;
      height = usableHeight;
    }
    const x = (pageWidth - width) / 2;
    pdf.addImage(imgData, "JPEG", x, margin, width, height);
  } else {
    // Fill the width; slice a too-tall image across multiple Letter pages.
    const width = usableWidth;
    const height = width / canvasRatio;
    if (height <= usableHeight + 0.02) {
      pdf.addImage(imgData, "JPEG", margin, margin, width, height);
    } else {
      let heightLeft = height;
      let position = margin;
      pdf.addImage(imgData, "JPEG", margin, position, width, height);
      heightLeft -= usableHeight;
      while (heightLeft > 0) {
        position = margin - (height - heightLeft); // shift the image up one page
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", margin, position, width, height);
        heightLeft -= usableHeight;
      }
    }
  }

  pdf.save(filename);
}
